<template>
  <div class="min-h-screen relative overflow-hidden">
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat"
      :style="{ backgroundImage: `url(${backgroundImage})` }"
    ></div>
    <div class="absolute inset-0 bg-black bg-opacity-70"></div>

    <div class="relative z-10 min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div class="w-full max-w-2xl">
        <div class="text-center">
          <div v-if="loading" class="space-y-6">
            <div class="flex justify-center">
              <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
            </div>
            <h2 class="text-3xl font-bold text-white">Verifying your email...</h2>
            <p class="text-lg text-gray-300">Please wait while we verify your account.</p>
          </div>

          <div v-else-if="success" class="space-y-6">
            <div class="flex justify-center">
              <div class="rounded-full bg-green-500 p-6">
                <svg class="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
            <h2 class="text-3xl font-bold text-white">Email verified successfully!</h2>
            <p class="text-lg text-gray-300">
              Your account is now active. You can log in to access your account.
            </p>
            <div class="flex justify-center">
              <button
                @click="goToLogin"
                class="px-12 py-3 bg-blue-600 text-white text-xl font-medium rounded-full hover:bg-blue-700 transition-colors"
              >
                Go to Login
              </button>
            </div>
          </div>

          <div v-else-if="error" class="space-y-6">
            <div class="flex justify-center">
              <div class="rounded-full bg-red-500 p-6">
                <svg class="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
            </div>
            <h2 class="text-3xl font-bold text-white">Verification failed</h2>
            <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              <p class="text-lg text-red-700 dark:text-red-300">{{ error }}</p>
            </div>
            <p class="text-lg text-gray-300">
              Please go to the login page and use the <strong class="text-white">"Resend Verification Email"</strong> button to get a new link.
            </p>
            <div class="flex justify-center gap-4">
              <button
                @click="goToHome"
                class="px-12 py-3 bg-gray-600 text-white text-xl font-medium rounded-full hover:bg-gray-700 transition-colors"
              >
                Go to Login
              </button>
            </div>
          </div>

          <div v-else class="space-y-6">
            <div class="flex justify-center">
              <div class="rounded-full bg-yellow-500 p-6">
                <svg class="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
            </div>
            <h2 class="text-3xl font-bold text-white">No verification token found</h2>
            <p class="text-lg text-gray-300">
              Please check your email and click the verification link.
            </p>
            <div class="flex justify-center">
              <button
                @click="goToHome"
                class="px-12 py-3 bg-gray-600 text-white text-xl font-medium rounded-full hover:bg-gray-700 transition-colors"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { emailVerificationService } from '@/services/api/emailVerificationService'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const success = ref(false)
const error = ref('')
const backgroundImage = '/src/assets/images/tire-bg2.webp'

let redirectTimeout: number | null = null

onMounted(async () => {
  const token = route.query.token as string

  if (!token) {
    return
  }

  loading.value = true

  try {
    await emailVerificationService.verifyEmail(token)
    success.value = true
    
    redirectTimeout = window.setTimeout(() => {
      goToLogin()
    }, 3000)
  } catch (err: any) {
    error.value = err.response?.data?.message || 'An error occurred during verification.'
  } finally {
    loading.value = false
  }
})

const goToLogin = () => {
  if (redirectTimeout) {
    clearTimeout(redirectTimeout)
  }
  router.push('/')
}

const goToHome = () => {
  router.push('/')
}
</script>
